import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, HelpCircle, Hospital, Briefcase, Stethoscope, Users,
  CreditCard, Shield, Cpu, Plug, FileCheck, ArrowRight, Link2, Check,
} from 'lucide-react';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema, faqSchema } from '../lib/seoSchemas';
import { Button } from '../components/ui/button';

/**
 * /faq — Frequently Asked Questions about CaraLoom 2.0.
 *
 * The questions and answers are written as the **single source of
 * truth** that gets parroted by Sales, Support, and the SEO crawlers
 * (FAQPage JSON-LD is emitted in <head>). Every q/a is in EN-US plain
 * voice — no marketing fluff, no jargon, and ALWAYS cite what's gated /
 * mocked / live so an evaluator never feels misled.
 */

const CATEGORIES = [
  { id: 'general',      label: 'General',          icon: HelpCircle },
  { id: 'facility',     label: 'Hospitals & SNFs', icon: Hospital },
  { id: 'provider',     label: 'Care Providers',   icon: Briefcase },
  { id: 'clinician',    label: 'Clinicians',       icon: Stethoscope },
  { id: 'family',       label: 'Families',         icon: Users },
  { id: 'pricing',      label: 'Pricing',          icon: CreditCard },
  { id: 'security',     label: 'Security & HIPAA', icon: Shield },
  { id: 'ai',           label: 'AI',               icon: Cpu },
  { id: 'integrations', label: 'Integrations',     icon: Plug },
  { id: 'compliance',   label: 'Compliance',       icon: FileCheck },
];

const FAQS = [
  // ── General ──────────────────────────────────────────────────────
  {
    cat: 'general', q: 'What is CaraLoom?',
    a: 'CaraLoom is the AI operating system for the post-acute care ecosystem. It connects four parties on a single workflow layer: healthcare facilities (hospitals, SNFs, ALFs, rehab, memory care), care providers (home health, hospice, palliative), independent licensed clinicians (RNs, NPs, PTs, OTs, SLPs, CNAs, social workers, chaplains, therapists), and the patients & families they serve.',
  },
  {
    cat: 'general', q: 'How is CaraLoom different from PointClickCare, Athena, Epic, or a staffing agency?',
    a: 'PointClickCare/Athena/Epic are point solutions inside a single facility. Staffing agencies serve a single discipline at a 40-60% take. CaraLoom is the workflow LAYER between them — one operational graph that handles discharge → placement → visit → claim → payout across organizations. We integrate with the EHRs; we don\'t replace them.',
  },
  {
    cat: 'general', q: 'Which product suites are in CaraLoom?',
    a: 'Six: Referrals (discharge orchestration), Workforce (clinician marketplace + credentialing), Operations (scheduling + executive dashboards), Compliance (survey-readiness + audit), Revenue Cycle (eligibility → claims → ERA), and Care (care plans + family circles + check-ins).',
  },
  {
    cat: 'general', q: 'Where is CaraLoom available?',
    a: 'CaraLoom is available nationally across the US. Clinician verification (license + NPI + background check) is state-specific; we currently support all 50 states.',
  },

  // ── Hospitals & SNFs ─────────────────────────────────────────────
  {
    cat: 'facility', q: 'How does the discharge orchestration engine work?',
    a: 'When the discharge planner posts a referral, the matching engine runs in 4 waves (verified rank-1, rank-2, rank-3, then a broadcast). Each wave times out automatically. If no clinician accepts after the staffing-gap threshold (~30 minutes), the referral auto-posts to the workforce shift board — broadening the supply pool from the top-ranked candidates to every verified in-state clinician.',
  },
  {
    cat: 'facility', q: 'What ROI should a hospital expect?',
    a: 'Hospitals using CaraLoom typically see a 0.5–1.2 day reduction in length-of-stay on placement-bottlenecked discharges. At an average $1,200–2,400/discharge in net hospital savings, the platform pays for itself within the first 2–3 routed placements.',
  },
  {
    cat: 'facility', q: 'Does CaraLoom integrate with our EHR (Epic, Cerner, Meditech)?',
    a: 'Yes — via FHIR R4. We support Epic\'s ADT discharge events, Cerner\'s patient summary, and Meditech\'s referral feeds. Full FHIR integration is included on the Hospital Discharge Platform tier ($8 PBPM + $35/discharge routed).',
  },
  {
    cat: 'facility', q: 'How is the per-discharge fee structured to avoid Stark/AKS exposure?',
    a: 'The per-discharge fee is structured as a clinical-quality-outcomes service contract (HRRP-aligned readmission reduction), not as a placement-volume referral fee. This avoids both Stark self-referral and Anti-Kickback Statute exposure. We make our outside legal counsel\'s opinion letter available under NDA on request.',
  },

  // ── Care Providers ──────────────────────────────────────────────
  {
    cat: 'provider', q: 'How is provider pricing structured?',
    a: 'Per active patient per month (PMPM). Essentials at $7 PMPM (Referrals + Workforce), Growth at $11 PMPM (+ Operations + Compliance — most popular), Enterprise at $18 PMPM (+ RCM + AI Premium + Multi-location). 30-patient floor on Essentials & Growth. Per-seat ($99 clinical / $199 back-office) stacks on top.',
  },
  {
    cat: 'provider', q: 'Can multiple agency locations share one CaraLoom account?',
    a: 'Yes — on the Enterprise tier. Multi-location management lets a parent organization scope users, referrals, and reporting per facility while sharing a single billing entity, a single credentialing pool, and a single Operations dashboard.',
  },
  {
    cat: 'provider', q: 'What happens if my clinical census drops?',
    a: 'PMPM billing scales DOWN with census the same way it scales up. No flat-tier penalty for slow months. Below the 30-patient floor on Essentials & Growth, you continue paying the floor minimum.',
  },

  // ── Independent Clinicians ──────────────────────────────────────
  {
    cat: 'clinician', q: 'How much does CaraLoom take per visit?',
    a: 'A flat 12% per completed booking on the marketplace. Includes payment processing, malpractice/professional liability insurance, credential verification, AI matching, scheduling, secure messaging, and support. You keep 88%. No tier upgrades, no surprise fees.',
  },
  {
    cat: 'clinician', q: 'When do I get paid?',
    a: '1–2 business days after the SOAP note is signed (verified-settlement). Faster than the 2–4 week net pay typical at agencies. Instant payout option available at a 1.5% fee.',
  },
  {
    cat: 'clinician', q: 'What\'s included in credentialing?',
    a: 'License verification (NPI + state board), DEA registration (if applicable), background check (Checkr), OIG/SAM exclusion screening, malpractice insurance certificate, board certifications, and references. We re-verify every 365 days automatically.',
  },
  {
    cat: 'clinician', q: 'Do I need my own malpractice insurance?',
    a: 'No — basic professional liability coverage is included in the 12% service rate while you\'re working on CaraLoom-routed visits. Higher coverage limits and personal/off-platform coverage remain your own responsibility.',
  },
  {
    cat: 'clinician', q: 'Can I bring my own patients to CaraLoom?',
    a: 'Yes. The "direct booking" feature lets you import an existing patient, send them a magic-link onboarding flow, and bill the visit through CaraLoom (still at 12%). This is the cleanest way to consolidate your private-pay book.',
  },

  // ── Families ────────────────────────────────────────────────────
  {
    cat: 'family', q: 'Does CaraLoom cost anything for families?',
    a: 'Browsing and AI-powered recommendations are always free. We only charge a 6% booking fee on completed private-pay visits. Premium Membership ($39/month) is optional — it adds the AI Care Assistant, family dashboard, unlimited messaging, and priority support. Premium AUTO-PAUSES when there\'s no active patient, so you\'re never paying for nothing.',
  },
  {
    cat: 'family', q: 'What\'s a "Care Circle"?',
    a: 'A private, in-app group for the patient + their family + their assigned clinician(s). It auto-creates the moment a patient has an active visit. You can invite siblings, the spouse, or a friend via magic link (no app download required). Messages stay private to the circle — neither CaraLoom support nor other clinicians see them.',
  },
  {
    cat: 'family', q: 'How does the "What Changed This Week" digest work?',
    a: 'A 7-day pulse delivered to the family\'s portal every Sunday. Aggregates completed visits, daily check-ins, mood trends, care-plan changes, new circle members, and any readmission-risk flags into a single page. Read-only — no PHI leaves the family\'s authenticated session.',
  },
  {
    cat: 'family', q: 'How does CaraLoom verify clinicians for me?',
    a: 'Every clinician is identity-checked (government ID), license-verified at the state board level, NPI-validated, background-checked (Checkr), and OIG/SAM-screened. The "Verified" badge on a profile means all five layers have passed within the last 365 days.',
  },

  // ── Pricing ─────────────────────────────────────────────────────
  {
    cat: 'pricing', q: 'How is your pricing different from a flat-tier SaaS plan?',
    a: 'CaraLoom prices to your buyer\'s economics — per active patient (providers), per seat (clinical users), per bed (hospitals), per managed life (payers), per completed booking (clinicians), per booking + episodic premium (families). Flat tiers punish growth; our model scales with the value you create.',
  },
  {
    cat: 'pricing', q: 'Where can I see my exact ARR contribution?',
    a: 'Go to /pricing/calculator. Plug in your patient census, seats, claims, beds, or managed lives. You\'ll see your exact monthly + annual contract value, layer-by-layer breakdown, 5-year projection, and a valuation-range estimate. No sales call required.',
  },
  {
    cat: 'pricing', q: 'What\'s the Payer VBC (PEPM) layer?',
    a: 'Per-member-per-month contracts on managed Medicare Advantage / Medicaid LTSS lives. Three tiers: $4.50 PEPM (Essentials — readmission risk + discharge orchestration), $8.50 PEPM (Advanced — adds care navigation + outcomes attribution), $14 PEPM (Premium — adds custom VBC analytics + risk-share). Sold to health plans, not to providers.',
  },
  {
    cat: 'pricing', q: 'Are there annual contracts or volume discounts?',
    a: 'Month-to-month on SMB tiers (Essentials, Growth). Annual contracts on Enterprise tier unlock a 15% list-price discount + dedicated implementation services. Volume discounts on the Hospital and Payer tiers are negotiated case-by-case.',
  },

  // ── Security & HIPAA ────────────────────────────────────────────
  {
    cat: 'security', q: 'Is CaraLoom HIPAA-compliant?',
    a: 'CaraLoom is built to HIPAA standards: end-to-end encryption (in transit + at rest), least-privilege access controls, row-level multi-tenancy hard gates (one org\'s PHI is mathematically inaccessible to another), and a full PHI audit log on every read/write. We sign BAAs with every covered-entity customer + every subprocessor.',
  },
  {
    cat: 'security', q: 'What\'s the SOC 2 status?',
    a: 'SOC 2 Type II is in progress (target: Q4 2026). HITRUST CSF is on the 2027 roadmap. Real-time evidence collection is exposed at /trust/evidence for qualified enterprise prospects under NDA.',
  },
  {
    cat: 'security', q: 'How is patient data isolated between organizations?',
    a: 'Row-level hard gates enforce `org_id` on every query, every aggregation, every search. The `_tenancy.py` middleware is a deny-by-default firewall: if a query doesn\'t carry the user\'s org_id scope explicitly, it returns zero rows. This is regression-tested in `test_tenancy_hard_gate.py` and runs on every deployment.',
  },
  {
    cat: 'security', q: 'Where is the data hosted?',
    a: 'Currently on Emergent\'s HIPAA-eligible cloud (US-East). Migration to AWS (us-east-1 + us-west-2 multi-AZ) is on the Q2 2026 roadmap, after BAA renegotiation with our infrastructure subprocessors.',
  },

  // ── AI ──────────────────────────────────────────────────────────
  {
    cat: 'ai', q: 'Which AI models does CaraLoom use?',
    a: 'A mix of frontier LLMs depending on the task: Claude Sonnet for clinical-grade SOAP drafting + care plan generation, Gemini Flash for routine summarization, OpenAI GPT for CPT coding suggestions. All routed through the Emergent LLM gateway with our AI Audit Envelope wrapping every call — every input + output gets logged for compliance review.',
  },
  {
    cat: 'ai', q: 'Will AI ever auto-sign a clinical note or auto-submit a claim?',
    a: 'No. AI ALWAYS produces drafts. A licensed clinician must review and sign every clinical note. A human RCM operator must review and submit every claim. The AI Audit Envelope makes this auditable — every AI-drafted artifact carries a `requires_human_review` flag that blocks the downstream billing flow until cleared.',
  },
  {
    cat: 'ai', q: 'Do you train models on customer PHI?',
    a: 'No. We do NOT use customer PHI to train, fine-tune, or improve any model. Our LLM contracts with OpenAI, Anthropic, and Google explicitly exclude API inputs from training. The AI Audit Envelope logs are stored encrypted and only accessible to the customer org + Emergent\'s security team under court order.',
  },

  // ── Integrations ────────────────────────────────────────────────
  {
    cat: 'integrations', q: 'Which EHRs do you integrate with?',
    a: 'Epic, Cerner (Oracle Health), Meditech, Athenahealth, eClinicalWorks, and PointClickCare. All via FHIR R4. Custom HL7 v2 integrations are available on the Enterprise tier.',
  },
  {
    cat: 'integrations', q: 'Does CaraLoom support real-time eligibility?',
    a: 'Yes — via Availity for commercial + Medicare Advantage, and via CMS HETS for traditional Medicare. Eligibility runs automatically when a referral lands and is re-verified 24 hours before each visit.',
  },
  {
    cat: 'integrations', q: 'How do claims get filed?',
    a: 'Through the 837P / 837I EDI standard via the Stedi gateway (when live keys are provisioned — currently mocked for new customers; existing customers run on a partner clearinghouse). ERA 835 remittances auto-post to the claim ledger via /api/rcm/era-835.',
  },
  {
    cat: 'integrations', q: 'Do you have a public API?',
    a: 'Yes — the v1 partner API exposes referrals, appointments, claims, eligibility, and audit-log endpoints. Pricing: free up to 50K calls/month, then $0.40/call + a platform fee of $499–1,999/month. White-label deployments include the full API plus a dedicated subdomain.',
  },

  // ── Compliance ──────────────────────────────────────────────────
  {
    cat: 'compliance', q: 'Does CaraLoom help with survey-readiness?',
    a: 'Yes. The Compliance Suite includes a Survey-Readiness Score that evaluates 40+ CMS-aligned criteria (documentation completeness, OASIS timing, plan-of-care signature timeliness, ICD coding accuracy, etc.) and surfaces gaps before the surveyor finds them. Available on Growth tier and above.',
  },
  {
    cat: 'compliance', q: 'How is the patient-survey (CG-CAHPS) data collected?',
    a: 'After every completed visit, the clinician\'s admin can issue a one-shot survey token via magic link. The family completes the CG-CAHPS-aligned 7-question survey without login. Aggregate NPS + response-quality scores are visible on the Operations dashboard and feed the public Trust Portal.',
  },
  {
    cat: 'compliance', q: 'Can I export a full audit trail for a Medicare ZPIC audit?',
    a: 'Yes — the Compliance audit-trail export ($5–15K/year add-on) generates a CMS-format-aligned audit package on demand: every PHI read, every clinical-note change, every claim submission, every credential verification stamp.',
  },
  {
    cat: 'compliance', q: 'How do you handle data subject access requests (HIPAA right of access)?',
    a: 'Patient self-service export at /privacy/dashboard — generates a complete PHI export within 24 hours (under the HIPAA 30-day requirement). Family/legal-rep requests require notarized authorization and are processed within 5 business days.',
  },
];

const Q_PER_CATEGORY = (cat) => FAQS.filter((f) => f.cat === cat).length;

/** Kebab-case slug from the question text — used in URL hashes for deep-linking. */
const slugify = (s) =>
  s.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

const FAQ_INDEX = FAQS.map((f, i) => ({ ...f, slug: slugify(f.q), idx: i }));

export default function FaqPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(searchParams.get('cat') || 'all');
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [openItem, setOpenItem] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const initialScrollDoneRef = useRef(false);

  // ── URL → state sync (back/forward nav) ────────────────────────────
  useEffect(() => {
    const urlCat = searchParams.get('cat') || 'all';
    const urlQ = searchParams.get('q') || '';
    if (urlCat !== activeCat) setActiveCat(urlCat);
    if (urlQ !== q) setQ(urlQ);
  }, [searchParams]);

  // ── state → URL sync (debounced for `q`) ───────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (activeCat && activeCat !== 'all') next.set('cat', activeCat);
      else next.delete('cat');
      if (q) next.set('q', q);
      else next.delete('q');
      // Only push if it actually changed (avoids history spam on mount).
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    }, 200);
    return () => clearTimeout(t);
  }, [activeCat, q]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = FAQ_INDEX;
    if (activeCat !== 'all') list = list.filter((f) => f.cat === activeCat);
    if (term) list = list.filter((f) => f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term));
    return list;
  }, [activeCat, q]);

  // ── On first render, if a #hash matches a slug, expand it + scroll into view.
  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    const hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) {
      initialScrollDoneRef.current = true;
      return;
    }
    const target = FAQ_INDEX.find((f) => f.slug === hash);
    if (target) {
      // Force the target to render (clear any active filter that would hide it).
      if (activeCat !== 'all' && activeCat !== target.cat) setActiveCat('all');
      setOpenItem(`${target.cat}-${target.idx}`);
      // Wait one tick so the accordion renders + lays out, then scroll.
      setTimeout(() => {
        const el = document.getElementById(`faq-${target.slug}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        initialScrollDoneRef.current = true;
      }, 120);
    } else {
      initialScrollDoneRef.current = true;
    }
  }, []);

  const copyLink = async (slug) => {
    try {
      const url = `${window.location.origin}/faq#${slug}`;
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug((s) => (s === slug ? null : s)), 1500);
    } catch {
      // Clipboard API unavailable — silently no-op.
    }
  };

  const seoSchema = useMemo(() => [
    organizationSchema(),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'FAQ', path: '/faq' }]),
    faqSchema(filtered.map((f) => ({ question: f.q, answer: f.a }))),
  ], [filtered]);

  return (
    <div className="min-h-screen bg-white dark:bg-cn-ink-900" data-testid="faq-page">
      <Seo
        title="Frequently Asked Questions — CaraLoom"
        description="Honest answers about CaraLoom 2.0 — the AI operating system for the post-acute care ecosystem. Hospitals, providers, clinicians, families, pricing, security, AI, integrations, and compliance."
        path="/faq"
        keywords={['CaraLoom FAQ', 'post-acute care platform', 'home health software FAQ', 'discharge planning FAQ']}
        schema={seoSchema}
      />
      <PublicNavbar />

      {/* HERO */}
      <section className="pt-14 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-cn-primary-hover text-white">
        <div className="max-w-7xl mx-auto">
          <div className="cn-eyebrow text-cn-accent mb-3 inline-flex items-center gap-2">
            <HelpCircle className="w-3.5 h-3.5" /> Frequently asked questions
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl !text-white max-w-3xl">
            Honest answers about CaraLoom.
          </h1>
          <p className="text-base sm:text-lg text-slate-300 mt-4 max-w-2xl">
            Direct answers to the questions hospitals, providers, clinicians, and families actually ask
            us — including what&apos;s live, what&apos;s mocked, and what&apos;s on the roadmap.
          </p>

          {/* Search */}
          <div className="mt-7 relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the FAQ…"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:border-cn-accent focus:bg-white/15 transition"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      {/* CATEGORY PILLS */}
      <section className="sticky top-0 bg-white dark:bg-cn-ink-900 z-20 border-b border-slate-100 dark:border-cn-border" data-testid="faq-category-pills">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap gap-2 overflow-x-auto">
          <CategoryPill
            id="all"
            label={`All (${FAQS.length})`}
            icon={HelpCircle}
            active={activeCat === 'all'}
            onClick={() => setActiveCat('all')}
          />
          {CATEGORIES.map((c) => (
            <CategoryPill
              key={c.id}
              id={c.id}
              label={`${c.label} (${Q_PER_CATEGORY(c.id)})`}
              icon={c.icon}
              active={activeCat === c.id}
              onClick={() => setActiveCat(c.id)}
            />
          ))}
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="py-14 px-4 sm:px-6 lg:px-8" data-testid="faq-list-section">
        <div className="max-w-3xl mx-auto">
          {filtered.length === 0 ? (
            <div className="rounded-2xl bg-cn-bone-50 border border-slate-100 p-8 text-center" data-testid="faq-empty">
              <HelpCircle className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
              <p className="font-semibold text-cn-text">No answers matched that search.</p>
              <p className="text-sm text-cn-text-muted mt-1">Try a different keyword, or ask us directly.</p>
              <Link to="/contact" className="inline-block mt-4">
                <Button className="bg-cn-primary text-white hover:bg-cn-primary-hover">
                  Talk to us <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <Accordion
              type="single"
              collapsible
              className="space-y-3"
              data-testid="faq-accordion"
              value={openItem || ''}
              onValueChange={(v) => {
                setOpenItem(v || null);
                // Push the slug into the URL hash so the user can copy/share the open answer.
                if (v) {
                  const [cat, idxStr] = v.split('-');
                  const idx = Number(idxStr);
                  const match = FAQ_INDEX.find((f) => f.cat === cat && f.idx === idx);
                  if (match) {
                    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${match.slug}`);
                  }
                } else if (window.location.hash) {
                  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
                }
              }}
            >
              {filtered.map((f) => {
                const cat = CATEGORIES.find((c) => c.id === f.cat);
                const value = `${f.cat}-${f.idx}`;
                const isOpen = openItem === value;
                return (
                  <AccordionItem
                    key={value}
                    value={value}
                    id={`faq-${f.slug}`}
                    data-testid={`faq-item-${f.cat}-${f.idx}`}
                    className="rounded-2xl bg-white dark:bg-cn-ink-800 border border-slate-100 dark:border-cn-border px-5 [&[data-state=open]]:border-cn-primary/40 [&[data-state=open]]:shadow-cn-subtle transition scroll-mt-32"
                  >
                    <AccordionTrigger className="hover:no-underline py-4 text-left">
                      <div className="flex items-start gap-3 pr-3">
                        {cat?.icon && (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-cn-primary/10 text-cn-primary shrink-0 mt-0.5">
                            <cat.icon className="w-3.5 h-3.5" />
                          </span>
                        )}
                        <span className="font-semibold text-cn-text dark:text-white text-base">{f.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pt-1 pl-10 text-cn-text-muted leading-relaxed">
                      <p>{f.a}</p>
                      {isOpen && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); copyLink(f.slug); }}
                          data-testid={`faq-copy-link-${f.slug}`}
                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-cn-primary hover:underline"
                        >
                          {copiedSlug === f.slug ? (
                            <><Check className="w-3 h-3" /> Link copied</>
                          ) : (
                            <><Link2 className="w-3 h-3" /> Copy direct link</>
                          )}
                        </button>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-cn-bone-50 dark:bg-cn-ink-800" data-testid="faq-cta-band">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="cn-display text-3xl sm:text-4xl text-cn-text dark:text-white mb-3">
            Still have questions?
          </h2>
          <p className="text-cn-text-muted mb-7 max-w-xl mx-auto">
            We answer every inbound under 24 hours. Engineering-grade detail, no funnel-bot replies.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-cn-primary text-white hover:bg-cn-primary-hover h-12 px-6 rounded-full font-semibold" data-testid="faq-contact-cta">
                Contact us <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing/calculator">
              <Button variant="outline" className="border-slate-300 h-12 px-6 rounded-full font-semibold" data-testid="faq-calculator-cta">
                See your exact pricing
              </Button>
            </Link>
            <Link to="/trust">
              <Button variant="outline" className="border-slate-300 h-12 px-6 rounded-full font-semibold" data-testid="faq-trust-cta">
                Trust portal
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function CategoryPill({ id, label, icon: Icon, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`faq-cat-${id}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition ${
        active
          ? 'bg-cn-primary text-white border-cn-primary shadow-sm'
          : 'bg-white text-cn-text border-slate-200 hover:border-cn-primary/50 dark:bg-cn-ink-800 dark:text-white dark:border-cn-border'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}
